import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "tiendify_cart_v1";
const ITEMS_EVENT = "tiendify:cart";
const OPEN_EVENT = "tiendify:cart-open";

const read = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
};

const write = (items) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        window.dispatchEvent(new CustomEvent(ITEMS_EVENT));
    } catch {
        // ignore
    }
};

export const useCart = () => {
    const [items, setItems] = useState(() => read());
    const [open, setOpenLocal] = useState(false);

    useEffect(() => {
        const onItems = () => setItems(read());
        const onOpen = (e) => setOpenLocal(Boolean(e.detail));
        window.addEventListener("storage", onItems);
        window.addEventListener(ITEMS_EVENT, onItems);
        window.addEventListener(OPEN_EVENT, onOpen);
        return () => {
            window.removeEventListener("storage", onItems);
            window.removeEventListener(ITEMS_EVENT, onItems);
            window.removeEventListener(OPEN_EVENT, onOpen);
        };
    }, []);

    const setOpen = useCallback((value) => {
        const v = typeof value === "function" ? value(false) : Boolean(value);
        setOpenLocal(v);
        window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: v }));
    }, []);

    const add = useCallback((product, quantity = 1, variant = null) => {
        const current = read();
        const key = `${product.id}__${variant || ""}`;
        const existing = current.find((i) => i.key === key);
        let next;
        if (existing) {
            next = current.map((i) =>
                i.key === key ? { ...i, quantity: i.quantity + quantity } : i
            );
        } else {
            next = [
                ...current,
                {
                    key,
                    productId: product.id,
                    storeId: product.storeId,
                    storeSlug: product.storeSlug,
                    storeName: product.storeName,
                    storeLocation: product.storeLocation,
                    name: product.name,
                    slug: product.slug,
                    image: product.images?.[0],
                    price: product.price,
                    variant,
                    quantity,
                },
            ];
        }
        write(next);
        setItems(next);
        setOpen(true);
    }, [setOpen]);

    const remove = useCallback((key) => {
        const next = read().filter((i) => i.key !== key);
        write(next);
        setItems(next);
    }, []);

    const updateQty = useCallback((key, quantity) => {
        if (quantity < 1) return;
        const next = read().map((i) =>
            i.key === key ? { ...i, quantity } : i
        );
        write(next);
        setItems(next);
    }, []);

    const clear = useCallback(() => {
        write([]);
        setItems([]);
    }, []);

    const count = items.reduce((acc, i) => acc + i.quantity, 0);
    const subtotal = items.reduce((acc, i) => acc + i.price * i.quantity, 0);

    const byStore = items.reduce((acc, i) => {
        if (!acc[i.storeId]) {
            acc[i.storeId] = {
                storeId: i.storeId,
                storeName: i.storeName,
                storeSlug: i.storeSlug,
                storeLocation: i.storeLocation,
                items: [],
                subtotal: 0,
            };
        }
        acc[i.storeId].items.push(i);
        acc[i.storeId].subtotal += i.price * i.quantity;
        return acc;
    }, {});

    return {
        items,
        count,
        subtotal,
        groups: Object.values(byStore),
        open,
        setOpen,
        add,
        remove,
        updateQty,
        clear,
    };
};
